/**
 * D18 subdomain-based tenant entry — the SPA's half of the canonical-form contract.
 *
 * The browser cannot call the C# domain primitives (`TenantDomainHostname.Parse`,
 * `TenantAppOrigin.Parse`), so this is a second implementation by necessity of the
 * language boundary. It is bound to the same rules by a shared fixture —
 * `shared/fixtures/tenant-domain-canonical-form.json` — executed as a conformance
 * test on both sides (`tenantDomain.conformance.test.ts`). A divergence therefore
 * fails a test rather than surfacing as a tenant that cannot log in.
 *
 * If you change a rule here, change the fixture and the C# primitives with it.
 */

export const TenantDomainErrors = {
  ValueRequired: 'Nexo:Sso:TenantDomainValueRequired',
  InvalidSyntax: 'Nexo:Sso:TenantDomainInvalidSyntax',
  NonAscii: 'Nexo:Sso:TenantDomainNonAscii',
  HostnameCarriesUriParts: 'Nexo:Sso:TenantDomainHostnameCarriesUriParts',
  LabelTooLong: 'Nexo:Sso:TenantDomainLabelTooLong',
  HostnameTooLong: 'Nexo:Sso:TenantDomainHostnameTooLong',
  NotCanonical: 'Nexo:Sso:TenantDomainNotCanonical',
  SlugReserved: 'Nexo:Sso:TenantDomainSlugReserved',
  UriInvalid: 'Nexo:Sso:TenantDomainUriInvalid',
  UriSchemeNotSupported: 'Nexo:Sso:TenantDomainUriSchemeNotSupported',
} as const;

export const MAX_LABEL_LENGTH = 63;
export const MAX_HOSTNAME_LENGTH = 253;

/**
 * Names that can never be a tenant slug. `admin` is the reserved host-login entry
 * point and is on this list for exactly that reason. Deployment-specific operational
 * subdomains are added server-side; the SPA only needs the built-in set, because a
 * name the server also reserves simply resolves to nothing here.
 */
export const RESERVED_NAMES: readonly string[] = ['login', 'www', 'api', 'admin'];

export type ParseResult<T> = { ok: true; value: T } | { ok: false; error: string };

export interface ParsedHostname {
  /** The normalized value, e.g. `tenant1.nexo.com`. */
  value: string;
  /** The leftmost label — the tenant slug position, and what the reserved check applies to. */
  firstLabel: string;
}

const ok = <T>(value: T): ParseResult<T> => ({ ok: true, value });
const fail = <T>(error: string): ParseResult<T> => ({ ok: false, error });

const isAscii = (value: string): boolean => {
  for (let i = 0; i < value.length; i++) {
    if (value.charCodeAt(i) > 0x7f) return false;
  }
  return true;
};

const isAlphanumeric = (c: string): boolean =>
  (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9');

/** The one place the label rules live — shared by hostnames and slugs, as in C#. */
function validateLabel(label: string): string | null {
  if (label.length === 0) return TenantDomainErrors.InvalidSyntax;
  if (label.length > MAX_LABEL_LENGTH) return TenantDomainErrors.LabelTooLong;
  if (!isAlphanumeric(label[0]) || !isAlphanumeric(label[label.length - 1])) {
    // Must start and end alphanumeric — this is what rejects a leading or trailing
    // hyphen without a separate rule.
    return TenantDomainErrors.InvalidSyntax;
  }
  for (const c of label) {
    if (!isAlphanumeric(c) && c !== '-') return TenantDomainErrors.InvalidSyntax;
  }
  return null;
}

/**
 * Canonicalizing parse — the LOOKUP path. Lowercases and drops a single trailing dot
 * so an incoming hostname with odd casing resolves instead of failing closed.
 */
export function parseHostname(input: string | null | undefined): ParseResult<ParsedHostname> {
  if (input == null || input.trim().length === 0) {
    return fail(TenantDomainErrors.ValueRequired);
  }

  const trimmed = input.trim();

  if (!isAscii(trimmed)) {
    // ASCII-only for a single simple canonical model across routing, DNS,
    // certificates and operator review — not because IDNA is ambiguous.
    return fail(TenantDomainErrors.NonAscii);
  }

  // A hostname is a hostname: scheme, port, path, query, fragment and user-info are
  // REJECTED rather than stripped, so a caller that passes a URI gets an error
  // instead of a silently different tenant.
  if (/[:/?#@\\ ]/.test(trimmed)) {
    return fail(TenantDomainErrors.HostnameCarriesUriParts);
  }

  const lowered = trimmed.toLowerCase();
  // Only ONE trailing dot (the DNS root) is dropped: "a.b.." still has an empty label.
  const canonical = lowered.endsWith('.') ? lowered.slice(0, -1) : lowered;

  if (canonical.length === 0) return fail(TenantDomainErrors.ValueRequired);
  if (canonical.length > MAX_HOSTNAME_LENGTH) return fail(TenantDomainErrors.HostnameTooLong);

  const labels = canonical.split('.');
  for (const label of labels) {
    const error = validateLabel(label);
    if (error) return fail(error);
  }

  return ok({ value: canonical, firstLabel: labels[0] });
}

/**
 * Strict AUTHORING parse: the value must already BE the canonical form, so uppercase
 * and a trailing dot are refused rather than silently repaired.
 */
export function parseAuthoredHostname(input: string | null | undefined): ParseResult<ParsedHostname> {
  const parsed = parseHostname(input);
  if (!parsed.ok) return parsed;

  if (input!.trim() !== parsed.value.value) {
    return fail(TenantDomainErrors.NotCanonical);
  }
  return parsed;
}

export function isReservedName(normalizedName: string): boolean {
  return RESERVED_NAMES.includes(normalizedName);
}

/** Canonicalizing slug parse. The reserved check runs here and cannot be skipped. */
export function parseSlug(input: string | null | undefined): ParseResult<string> {
  if (input == null || input.trim().length === 0) {
    return fail(TenantDomainErrors.ValueRequired);
  }

  const trimmed = input.trim();
  if (!isAscii(trimmed)) return fail(TenantDomainErrors.NonAscii);

  const canonical = trimmed.toLowerCase();

  const error = validateLabel(canonical);
  if (error) return fail(error);

  // AFTER normalization — otherwise "Login" would slip past.
  if (isReservedName(canonical)) return fail(TenantDomainErrors.SlugReserved);

  return ok(canonical);
}

/**
 * Canonicalize an absolute http(s) URI: lowercase scheme and host, the scheme's
 * default port omitted, no trailing slash. This is the form both stores are compared
 * in — OpenIddict compares its side as an ordinal string, so a casing or
 * trailing-slash difference would make the dual check disagree with itself.
 */
export function canonicalizeUri(input: string | null | undefined): ParseResult<string> {
  if (input == null || input.trim().length === 0) {
    return fail(TenantDomainErrors.ValueRequired);
  }

  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    return fail(TenantDomainErrors.UriInvalid);
  }

  const scheme = url.protocol.replace(':', '').toLowerCase();
  if (scheme !== 'http' && scheme !== 'https') {
    return fail(TenantDomainErrors.UriSchemeNotSupported);
  }

  if (url.username || url.password) {
    return fail(TenantDomainErrors.HostnameCarriesUriParts);
  }

  if (url.search || url.hash) {
    return fail(TenantDomainErrors.UriInvalid);
  }

  const host = parseHostname(url.hostname);
  if (!host.ok) return fail(host.error);

  // URL normalizes a default port to '' already; an explicit non-default one stays.
  const port = url.port && !isDefaultPort(scheme, url.port) ? `:${url.port}` : '';
  const path = url.pathname.replace(/\/+$/, '');

  return ok(`${scheme}://${host.value.value}${port}${path}`);
}

function isDefaultPort(scheme: string, port: string): boolean {
  return (scheme === 'https' && port === '443') || (scheme === 'http' && port === '80');
}

/**
 * The origin the SPA is running on, canonicalized. This is what the tenant's
 * redirect_uri is built from, and what the auth host looks up in `TenantDomain`.
 */
export function currentOrigin(location: Location = window.location): string {
  const canonical = canonicalizeUri(location.origin);
  return canonical.ok ? canonical.value : location.origin;
}

/**
 * The tenant slug this browser is on, or null when the hostname is not a tenant
 * subdomain (the apex, `www`, `localhost`, the reserved host-login subdomain, or an
 * unmapped name). Null means "fall through to the neutral entry point" — this
 * function deliberately does NOT decide whether the tenant exists; only the server
 * can answer that, and it answers active, inactive and unknown identically.
 */
export function tenantSlugFromHostname(hostname: string): string | null {
  const parsed = parseHostname(hostname);
  if (!parsed.ok) return null;

  const labels = parsed.value.value.split('.');
  // A bare host (`localhost`) is not a tenant subdomain: there is no label in front
  // of anything. The strict shape is `{slug}.{something}`.
  if (labels.length < 2) return null;

  const slug = parseSlug(parsed.value.firstLabel);
  return slug.ok ? slug.value : null;
}

/** True when the browser is on the reserved host-login subdomain. */
export function isHostLoginHostname(hostname: string, hostLoginSlug = 'admin'): boolean {
  const parsed = parseHostname(hostname);
  return parsed.ok && parsed.value.firstLabel === hostLoginSlug;
}

/**
 * True when the HOSTNAME decides the tenant — the condition under which the SPA must
 * not send a `__tenant` hint.
 *
 * It takes two facts, not one. The shape of the hostname says a subdomain COULD carry
 * a tenant; whether this installation routes login that way is
 * `subdomainEntry` (see `services/tenantEntryConfig`). Judging on shape alone reads
 * every custom domain — `nexo-test.example.com`, `nexo.customer.se` — as a tenant
 * subdomain, and an installation without an origin template then suppresses the hint
 * that is the only thing routing its login: the tenant a user picks never reaches
 * /connect/authorize, the ABP login page opens on the host, and no tenant user can
 * sign in at all.
 *
 * `subdomainEntry === null` means the installation has not answered; the hostname
 * alone decides, which keeps a real tenant subdomain behaving as it does today.
 */
export function isTenantBoundHostname(
  hostname: string,
  subdomainEntry: boolean | null,
): boolean {
  if (subdomainEntry === false) return false;

  return isHostLoginHostname(hostname) || tenantSlugFromHostname(hostname) !== null;
}
